import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowTopRightOnSquareIcon, CheckIcon, XMarkIcon } from 'components/icons';
import { opportunityApi, pipelineApi, MatchRow } from '../../services/api';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import { SkeletonRows } from '../ui/Skeleton';

const dueText = (iso: string | null) => {
  if (!iso) return 'No deadline listed';
  const d = new Date(iso);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days < 0) return `Closed ${date}`;
  if (days === 0) return 'Due today';
  return `Due ${date} · ${days} day${days === 1 ? '' : 's'}`;
};

const matchedText = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
};

/**
 * What the matcher found for this user. Each row can be opened, added to the
 * pipeline, or dismissed; dismissed rows stay reachable under their own tab.
 */
const VIEW_DOT: Record<string, string> = {
  slate: 'bg-slate-500', blue: 'bg-blue-500', purple: 'bg-purple-500', amber: 'bg-amber-500',
  green: 'bg-green-500', teal: 'bg-teal-500', indigo: 'bg-indigo-500', rose: 'bg-rose-500',
};

export default function MatchesPanel({ onCountChange, onEditCriteria, isFreePlan, reloadToken = 0 }: {
  onCountChange: (newCount: number) => void;
  onEditCriteria: () => void;
  isFreePlan: boolean;
  /** Bump after the criteria were edited elsewhere so the paused/view state re-reads. */
  reloadToken?: number;
}) {
  const [status, setStatus] = useState<'new' | 'dismissed'>('new');
  const navigate = useNavigate();
  const [rows, setRows] = useState<MatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // null until the config loads; Free has no config to read.
  const [paused, setPaused] = useState<boolean | null>(null);
  // The saved view the criteria were promoted from, when there is one.
  const [sourceView, setSourceView] = useState<{ id: string; name: string; color: string } | null>(null);
  const [watchBehind, setWatchBehind] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const LIMIT = 25;

  useEffect(() => {
    if (isFreePlan) return;
    pipelineApi.getConfig()
      .then((r) => { const c = r.data?.data || r.data?.config; setPaused(c?.watchPaused === true); setSourceView(c?.sourceView || null); setWatchBehind(c?.watchBehind === true); })
      .catch(() => {});
  }, [isFreePlan, reloadToken]);

  const setWatching = async (watching: boolean) => {
    setTogglingPause(true);
    try {
      await pipelineApi.updateConfig({ watchPaused: !watching });
      setPaused(!watching);
      toast.success(watching ? 'Watching again. New postings that fit your criteria will land here.' : 'Stopped watching. Your criteria are kept; nothing new will be recorded or emailed until you resume.', { duration: 6000 });
    } catch {
      toast.error(watching ? "Couldn't resume watching" : "Couldn't stop watching");
    } finally {
      setTogglingPause(false);
    }
  };

  const load = useCallback(async (p: number, append = false) => {
    setLoading(true); setError(null);
    try {
      const r = await opportunityApi.getMatches({ status, page: p, limit: LIMIT });
      setRows(prev => (append ? [...prev, ...r.data.matches] : r.data.matches));
      setTotal(r.data.total);
      setPage(p);
      setNewCount(r.data.newCount);
      onCountChange(r.data.newCount);
    } catch {
      setError("Couldn't load your matches.");
    } finally {
      setLoading(false);
    }
  }, [status, onCountChange]);

  useEffect(() => { load(1); }, [load]);

  const dismiss = async (row: MatchRow, dismissed: boolean) => {
    setBusy(row.alertId);
    try {
      await opportunityApi.dismissMatch(row.alertId, dismissed);
      setRows(prev => prev.filter(r => r.alertId !== row.alertId));
      setTotal(t => Math.max(0, t - 1));
      const next = Math.max(0, newCount + (dismissed ? -1 : 1));
      setNewCount(next);
      onCountChange(next);
    } catch {
      toast.error(dismissed ? "Couldn't dismiss that match" : "Couldn't restore that match");
    } finally {
      setBusy(null);
    }
  };

  const addToPipeline = async (row: MatchRow) => {
    setBusy(row.alertId);
    try {
      await pipelineApi.addOpportunity(row.opportunityId);
      setRows(prev => prev.map(r => (r.alertId === row.alertId ? { ...r, inPipeline: true } : r)));
      // Adding creates the pursuit's workspace; land there rather than toast.
      navigate(`/pursuits/${row.opportunityId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Couldn't add to pipeline");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-border">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div role="tablist" aria-label="Match status" className="flex gap-1">
          {(['new', 'dismissed'] as const).map(s => (
            <button
              key={s}
              role="tab"
              aria-selected={status === s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-md text-sm ${status === s ? 'bg-surface-sunk text-content font-medium' : 'text-content-muted hover:text-content'}`}
            >
              {s === 'new' ? 'New' : 'Dismissed'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {!loading && <span className="text-sm text-content-muted">{total.toLocaleString()} {status === 'new' ? 'open' : 'dismissed'}</span>}
          {sourceView ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-content-muted">
              Watching:
              <Link to={`/opportunities?set=${sourceView.id}`} className="inline-flex items-center gap-1.5 font-semibold text-accent hover:text-accent-hover">
                <span className={`h-2 w-2 rounded-full ${VIEW_DOT[sourceView.color] || 'bg-slate-500'}`} aria-hidden="true" />{sourceView.name}
              </Link>
              {watchBehind && <span className="text-warning" title="The view was edited after it was promoted; the watch still uses the earlier criteria">· behind the view</span>}
              <span aria-hidden="true">·</span>
              <Link to={`/opportunities?set=${sourceView.id}`} className="underline hover:text-content">Edit view</Link>
            </span>
          ) : (
            <button type="button" onClick={onEditCriteria} className="text-sm text-accent underline hover:text-accent-hover">Edit what I'm watching</button>
          )}
          {paused !== null && (
            <button
              type="button"
              onClick={() => setWatching(paused)}
              disabled={togglingPause}
              className="text-sm text-content-muted underline hover:text-content disabled:opacity-50"
              title={paused ? 'Start recording new matches again' : 'Keep your criteria but stop recording new matches and sending the daily email'}
            >
              {togglingPause ? 'Saving…' : paused ? 'Resume watching' : 'Stop watching'}
            </button>
          )}
        </div>
      </div>
      {paused && (
        <div role="status" className="px-4 py-2 text-sm text-content-muted bg-surface-sunk border-b border-border">
          Watching is paused. Nothing new is being recorded or emailed; what matched before is still listed below.
        </div>
      )}

      {error ? (
        <div className="p-6"><ErrorState title="Couldn't load matches" description={error} onRetry={() => load(1)} retrying={loading} compact /></div>
      ) : loading && rows.length === 0 ? (
        <div className="p-4"><SkeletonRows rows={5} /></div>
      ) : rows.length === 0 ? (
        <div className="p-8">
          <EmptyState
            title={status === 'new' ? 'No new matches yet' : 'Nothing dismissed'}
            description={
              isFreePlan
                ? 'Watching for matches is part of the Pro plan.'
                : status === 'new'
                  ? 'Set what to watch for — NAICS codes, states, agencies, set-asides — and every new SAM.gov posting that fits all of them lands here, with an email each morning.'
                  : 'Matches you dismiss show up here, in case you change your mind.'
            }
            actionLabel={status === 'new' && !isFreePlan ? 'Choose what to watch' : undefined}
            onAction={status === 'new' && !isFreePlan ? onEditCriteria : undefined}
          />
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map(row => (
            <li key={row.alertId} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex-1 min-w-0">
                <Link to={`/opportunities/${row.opportunityId}`} className="text-sm font-medium text-brand-600 hover:text-brand-900 line-clamp-2" title={row.title}>{row.title}</Link>
                <p className="text-xs text-content-muted mt-0.5 truncate">
                  {[row.department, row.naics && `NAICS ${row.naics}`, row.setAside && row.setAside !== 'NONE' && `Set-aside: ${row.setAside}`, row.state, row.type].filter(Boolean).join(' · ')}
                </p>
                <p className="text-xs mt-0.5">
                  <span className={row.responseDeadline && new Date(row.responseDeadline).getTime() - Date.now() < 7 * 86400000 ? 'text-warning font-medium' : 'text-content-muted'}>{dueText(row.responseDeadline)}</span>
                  <span className="text-content-subtle"> · matched {matchedText(row.matchedAt)}</span>
                </p>
                {row.reasons.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {row.reasons.slice(0, 4).map(r => <span key={r} className="px-1.5 py-0.5 rounded bg-surface-sunk text-caption text-content-muted">{r}</span>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.inPipeline ? (
                  <span className="inline-flex items-center gap-1 text-xs text-success"><CheckIcon className="h-4 w-4" /> In pipeline</span>
                ) : (
                  <button type="button" disabled={busy === row.alertId || isFreePlan} onClick={() => addToPipeline(row)} className="btn-secondary text-xs px-2.5 py-1.5" title={isFreePlan ? 'Pipeline requires the Pro plan' : undefined}>Add to pipeline</button>
                )}
                <Link to={`/opportunities/${row.opportunityId}`} className="p-1.5 rounded text-content-muted hover:text-content" aria-label={`Open ${row.title}`}><ArrowTopRightOnSquareIcon className="h-4 w-4" /></Link>
                {status === 'new' ? (
                  <button type="button" disabled={busy === row.alertId} onClick={() => dismiss(row, true)} className="p-1.5 rounded text-content-muted hover:text-content" aria-label={`Dismiss ${row.title}`} title="Dismiss"><XMarkIcon className="h-4 w-4" /></button>
                ) : (
                  <button type="button" disabled={busy === row.alertId} onClick={() => dismiss(row, false)} className="text-xs text-accent underline" aria-label={`Restore ${row.title}`}>Restore</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 && rows.length < total && (
        <div className="px-4 py-3 border-t border-border text-center">
          <button type="button" onClick={() => load(page + 1, true)} disabled={loading} className="btn-secondary text-sm">{loading ? 'Loading…' : `Show more (${(total - rows.length).toLocaleString()} left)`}</button>
        </div>
      )}
    </div>
  );
}
